// @file Surface comment attachments store
import { vDel, vSet } from '@@/bits/vue'
import type { Cid, CommentId } from '@@/types'
import { defineStore } from 'pinia'
import { ref } from 'vue'

export enum CommentAttachmentState {
  /** When the comment is in its normal state before picking attachment. */
  IDLE = 'idle',
  /** When the comment attachment is being loaded. */
  LOADING = 'loading',
  /** When the comment attachment has finished loading and is showing as a preview for the user to decide before submitting.  */
  PREVIEW = 'preview',
  /** When the user is dragging something to the comment. */
  DRAGGING = 'dragging',
}

export const useSurfaceCommentAttachmentsStore = defineStore('surfaceCommentAttachments', () => {
  /* ------------------------ */
  /* COMMENT ATTACHMENT STATE */
  /* ------------------------ */

  // This map is used for unsubmitted comments only (that's why the key is post CID -- each post may only have one unsubmitted comment).
  const newCommentAttachmentStateByPostCid = ref<Record<Cid, CommentAttachmentState>>({})

  const updateNewCommentAttachmentState = ({
    postCid,
    state,
  }: {
    postCid: Cid
    state: CommentAttachmentState
  }): void => {
    vSet(newCommentAttachmentStateByPostCid.value, postCid, state)
  }

  const removeNewCommentAttachmentState = ({ postCid }: { postCid: Cid }): void => {
    vDel(newCommentAttachmentStateByPostCid.value, postCid)
  }

  const newCommentAttachmentStateForPostCid = (postCid: Cid): CommentAttachmentState => {
    return newCommentAttachmentStateByPostCid.value[postCid] ?? CommentAttachmentState.IDLE
  }

  // This map stores the attachment state of comments being edited.
  const editedCommentAttachmentStateById = ref<Record<CommentId, CommentAttachmentState>>({})

  const updateEditedCommentAttachmentState = ({
    commentId,
    state,
  }: {
    commentId: CommentId
    state: CommentAttachmentState
  }): void => {
    vSet(editedCommentAttachmentStateById.value, commentId, state)
  }

  const removeEditedCommentAttachmentState = ({ commentId }: { commentId: CommentId }): void => {
    vDel(editedCommentAttachmentStateById.value, commentId)
  }

  const editedCommentAttachmentStateForId = (commentId: CommentId): CommentAttachmentState => {
    return editedCommentAttachmentStateById.value[commentId] ?? CommentAttachmentState.IDLE
  }

  return {
    // Comment attachment state
    newCommentAttachmentStateByPostCid,
    updateNewCommentAttachmentState,
    removeNewCommentAttachmentState,
    newCommentAttachmentStateForPostCid,
    editedCommentAttachmentStateById,
    updateEditedCommentAttachmentState,
    removeEditedCommentAttachmentState,
    editedCommentAttachmentStateForId,
  }
})
